import React, { useEffect, useRef, useState } from "react";
import "./ReorderModal.scss";
import { Modal, Typography, Button, Spin, Image } from "antd";
import { ReloadOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import {
  getReorderPreview,
  placeReorder,
} from "../../features/orders/ordersActions";
import { resetReorder } from "../../features/orders/ordersSlice";
import product_logo from "../../assets/logo_product.svg";
import Utils from "../../utils";

export const FALLBACK_ERROR = "This order cannot be reordered right now.";

const STEP_PREVIEW = "preview";
const STEP_ERROR = "error";

const PAYMENT_LABELS = {
  cash: "Cash",
  cashless: "Cashless",
  apple_cash: "Apple Cash",
  apple_pay: "Apple Pay",
  credit_card: "Credit card",
  zelle: "Zelle",
  venmo: "Venmo",
  crypto: "Crypto",
};

const getPaymentLabel = (paymentType) => {
  if (!paymentType) return "";
  return PAYMENT_LABELS[paymentType] || String(paymentType)?.replace(/_/g, " ");
};

// Tag text for an unavailable line, from the server's `reason`. A product
// the store has hidden (`unpublished`) reads as out of stock: that the shop
// chose to hide it is not the customer's business.
export const getReasonTag = (item) => {
  switch (item?.reason) {
    case "deleted":
      return "No longer sold";
    case "not_on_menu":
      return "Not on the shop menu";
    case "unpublished":
      return "Out of stock";
    case "out_of_stock":
      return item?.available_count > 0
        ? `Only ${item?.available_count} left`
        : "Out of stock";
    default:
      return "Unavailable";
  }
};

// One sentence out of a rejected payload. The 400 shapes are
// `{ non_field_errors: [...] , items: [...] }`, `{ detail }` and
// `{ address: [...] }`; anything else falls back to the generic text so the
// graded `items` list never gets dumped into the dialog.
export const getReorderErrorMessage = (payload) => {
  if (!payload) return FALLBACK_ERROR;
  if (typeof payload === "string") return payload;
  if (typeof payload !== "object") return FALLBACK_ERROR;
  const nonField = payload?.non_field_errors;
  if (Array.isArray(nonField) && nonField[0]) {
    return Utils?.getErrorText(nonField[0]) || FALLBACK_ERROR;
  }
  if (typeof nonField === "string" && nonField) return nonField;
  if (payload?.detail) return Utils?.getErrorText(payload?.detail);
  if (payload?.message && typeof payload?.message === "string") {
    return payload?.message;
  }
  const address = payload?.address;
  if (Array.isArray(address) && address[0]) {
    return Utils?.getErrorText(address[0]);
  }
  const rest = { ...payload };
  delete rest.items;
  return Utils?.getErrorText(rest) || FALLBACK_ERROR;
};

const money = (value) => Utils?.roundNumber(Utils?.parsePrice(value));

const isZero = (value) => Utils?.parsePrice(value) === 0;

// Quick Reorder dialog for one past order. `order` is the history row the
// customer pressed Reorder on (null = closed). Opening fetches the preview
// (today's prices, stock, totals); Place Order posts it again and hands the
// new row to `onPlaced`. Two steps: the preview, and an error screen for a
// refused preview or a failed placement.
export default function ReorderModal({ order, token, onCancel, onPlaced }) {
  const dispatch = useDispatch();
  const { loading, placing, preview } =
    useSelector((state) => state?.orders?.reorder) || {};
  const shop = useSelector((state) => state?.shopDetails?.shop);
  const storeName =
    shop?.name && shop?.is_name_visible_on_shop ? shop?.name : "the store";
  const [step, setStep] = useState(STEP_PREVIEW);
  const [errorMessage, setErrorMessage] = useState("");
  // Set while a POST is in flight so a double click can never place two
  // orders, even before the store's `placing` flag has re-rendered.
  const processingRef = useRef(false);

  const isOpened = Boolean(order?.id);
  const orderId = order?.id;

  useEffect(() => {
    if (!orderId) {
      return;
    }
    setStep(STEP_PREVIEW);
    setErrorMessage("");
    processingRef.current = false;
    dispatch(getReorderPreview(orderId))?.then((res) => {
      if (res?.type === "orders/reorderPreview/rejected") {
        setErrorMessage(getReorderErrorMessage(res?.payload));
        setStep(STEP_ERROR);
      } else if (
        res?.type === "orders/reorderPreview/fulfilled" &&
        (!res?.payload || typeof res?.payload !== "object")
      ) {
        // A 200 that is not the preview JSON (a login page, an empty body):
        // there is nothing to show, so treat it as refused.
        setErrorMessage(FALLBACK_ERROR);
        setStep(STEP_ERROR);
      }
    });
  }, [orderId, dispatch]);

  const close = () => {
    if (processingRef.current) {
      return;
    }
    dispatch(resetReorder());
    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  const canPlace =
    Boolean(preview?.can_reorder) &&
    !loading &&
    !placing &&
    !processingRef.current;

  const placeOrder = () => {
    if (!canPlace || processingRef.current) {
      return;
    }
    processingRef.current = true;
    dispatch(placeReorder({ orderId }))?.then((res) => {
      processingRef.current = false;
      if (res?.type === "orders/reorderPlace/fulfilled") {
        dispatch(resetReorder());
        if (typeof onPlaced === "function") {
          onPlaced(res?.payload);
        }
      } else {
        setErrorMessage(getReorderErrorMessage(res?.payload));
        setStep(STEP_ERROR);
      }
    });
  };

  const items = preview?.items || [];
  const totals = preview?.totals;
  const promo = preview?.promo_code;
  const taxes =
    totals?.taxes && typeof totals?.taxes === "object"
      ? Object.entries(totals?.taxes)
      : [];
  const paymentLabel = getPaymentLabel(preview?.payment_type);

  const renderSummaryRow = (label, value, extra = {}) => (
    <div
      className="reorderModal-cont__summary__row"
      key={extra?.key || label}>
      <Typography.Text
        className={classNames("reorderModal-cont__summary__row__label", {
          "reorderModal-cont__summary__row__label--total": extra?.total,
        })}
        style={{
          color:
            extra?.color ||
            (extra?.total ? token?.Typography?.mainColorText : undefined),
        }}>
        {label}
      </Typography.Text>
      <Typography.Text
        className={classNames("reorderModal-cont__summary__row__value", {
          "reorderModal-cont__summary__row__value--total": extra?.total,
        })}
        style={{
          color: extra?.color || token?.Typography?.mainColorText,
        }}>
        {value}
      </Typography.Text>
    </div>
  );

  const renderItem = (item, index) => {
    const unavailable = item?.status === "unavailable";
    const hasPrice =
      item?.sale_price !== null && item?.sale_price !== undefined;
    const onSale =
      hasPrice &&
      item?.price !== null &&
      item?.price !== undefined &&
      Utils?.parsePrice(item?.sale_price) !== Utils?.parsePrice(item?.price);
    // "Was $42 last time" only when the price moved since the old order.
    const priceChanged =
      hasPrice &&
      item?.old_price !== null &&
      item?.old_price !== undefined &&
      Utils?.parsePrice(item?.old_price) !==
        Utils?.parsePrice(item?.sale_price);
    return (
      <div
        key={`${item?.variant ?? "line"}-${index}`}
        className={classNames("reorderModal-cont__item", {
          "reorderModal-cont__item--unavailable": unavailable,
        })}
        style={{
          backgroundColor: token?.colorFillTertiary,
        }}>
        <div className="reorderModal-cont__item__imageWrapper">
          <Image
            alt={item?.name || "product"}
            src={item?.picture_url ? item?.picture_url : product_logo}
            preview={true}
            width="100%"
            height="100%"
            style={{
              objectFit: "contain",
            }}
          />
        </div>
        <div className="reorderModal-cont__item__body">
          <div className="reorderModal-cont__item__body__nameRow">
            <Typography.Text
              className="reorderModal-cont__item__body__name"
              style={{
                color: token?.Typography?.mainColorText,
              }}
              ellipsis={{
                tooltip: {
                  title: item?.name || "",
                },
              }}>
              {item?.name || ""}
            </Typography.Text>
            {unavailable ? (
              <span
                className="reorderModal-cont__item__body__tag"
                style={{
                  color: token?.colorError,
                }}>
                {getReasonTag(item)}
              </span>
            ) : null}
          </div>
          <div className="reorderModal-cont__item__body__priceRow">
            {hasPrice ? (
              <>
                {onSale ? (
                  <Typography.Text
                    delete
                    className="reorderModal-cont__item__body__priceRow__oldPrice"
                    style={{
                      color: token?.Typography?.colorPrimaryText,
                    }}>
                    {money(item?.price)}
                  </Typography.Text>
                ) : null}
                <Typography.Text
                  className="reorderModal-cont__item__body__priceRow__price"
                  style={{
                    color: token?.Typography?.mainColorText,
                  }}>
                  {money(item?.sale_price)}
                </Typography.Text>
              </>
            ) : null}
            <Typography.Text className="reorderModal-cont__item__body__priceRow__muted">
              {item?.quantity ?? 0}x
            </Typography.Text>
            {item?.variant_name ? (
              <Typography.Text className="reorderModal-cont__item__body__priceRow__muted">
                {item?.variant_name}
              </Typography.Text>
            ) : null}
            {priceChanged ? (
              <Typography.Text className="reorderModal-cont__item__body__priceRow__muted">
                was {money(item?.old_price)}
              </Typography.Text>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderPreview = () => (
    <div className="reorderModal-cont__body">
      <div className="reorderModal-cont__meta">
        <Typography.Text className="reorderModal-cont__meta__text">
          Reordering from{" "}
          <span
            className="reorderModal-cont__meta__text__strong"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            {storeName}
          </span>
        </Typography.Text>
        <Typography.Text className="reorderModal-cont__meta__text">
          {items?.length} {items?.length === 1 ? "item" : "items"}
        </Typography.Text>
      </div>

      <div className="reorderModal-cont__items">{items?.map(renderItem)}</div>

      {preview?.address || paymentLabel ? (
        <div className="reorderModal-cont__details">
          {preview?.address ? (
            <Typography.Text
              className="reorderModal-cont__details__text"
              ellipsis={{
                tooltip: {
                  title: preview?.address,
                },
              }}>
              {preview?.fulfillment_method === "PICKUP"
                ? "Pickup"
                : "Delivery"}
              : {preview?.address}
            </Typography.Text>
          ) : null}
          {paymentLabel ? (
            <Typography.Text className="reorderModal-cont__details__text">
              Payment: {paymentLabel}
            </Typography.Text>
          ) : null}
        </div>
      ) : null}

      {totals ? (
        <div
          className="reorderModal-cont__summary"
          style={{
            backgroundColor: token?.colorFillTertiary,
          }}>
          <Typography.Text
            className="reorderModal-cont__summary__title"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            Order Summary
          </Typography.Text>
          {renderSummaryRow("Subtotal", money(totals?.sub_total))}
          {!isZero(totals?.delivery_fee)
            ? renderSummaryRow("Delivery Fee", money(totals?.delivery_fee))
            : null}
          {taxes?.length
            ? taxes?.map(([name, value]) =>
                renderSummaryRow(name, money(value), { key: `tax-${name}` })
              )
            : !isZero(totals?.tax_total)
            ? renderSummaryRow("Taxes", money(totals?.tax_total))
            : null}
          {!isZero(totals?.mix_and_match)
            ? renderSummaryRow(
                "Mix & Match",
                `-${money(totals?.mix_and_match)}`,
                { color: token?.Typography?.colorPrimaryText }
              )
            : null}
          {promo?.applied && !isZero(totals?.promo_code)
            ? renderSummaryRow(
                `Discount (${promo?.code})`,
                `-${money(totals?.promo_code)}`,
                { color: token?.Typography?.colorPrimaryText }
              )
            : null}
          {renderSummaryRow("Total", money(totals?.grand_total), {
            total: true,
          })}
        </div>
      ) : null}

      {promo && !promo?.applied ? (
        <Typography.Text className="reorderModal-cont__note">
          Promo {promo?.code} no longer applies
        </Typography.Text>
      ) : null}

      {preview && !preview?.can_reorder ? (
        <Typography.Text
          className="reorderModal-cont__blocked"
          style={{
            color: token?.colorError,
          }}>
          {preview?.message || FALLBACK_ERROR}
        </Typography.Text>
      ) : null}
    </div>
  );

  const renderError = () => (
    <div className="reorderModal-cont__error">
      <CloseCircleOutlined
        className="reorderModal-cont__error__icon"
        style={{
          color: token?.colorError,
        }}
      />
      <Typography.Text
        className="reorderModal-cont__error__title"
        style={{
          color: token?.Typography?.mainColorText,
        }}>
        Cannot reorder
      </Typography.Text>
      <Typography.Text className="reorderModal-cont__error__text">
        {errorMessage || FALLBACK_ERROR}
      </Typography.Text>
    </div>
  );

  return (
    <Modal
      className="reorderModal-cont"
      width={560}
      centered
      destroyOnHidden
      open={isOpened}
      mask={{ closable: !placing }}
      keyboard={!placing}
      closable={!placing}
      onCancel={close}
      title={
        step === STEP_ERROR ? null : (
          <div className="reorderModal-cont__titleWrapper">
            <ReloadOutlined
              className="reorderModal-cont__titleWrapper__icon"
              style={{
                color: token?.Typography?.colorPrimaryText,
              }}
            />
            <Typography.Text
              className="reorderModal-cont__titleWrapper__title"
              style={{
                color: token?.Typography?.mainColorText,
              }}>
              Quick Reorder
            </Typography.Text>
          </div>
        )
      }
      // No Cancel button: the close icon in the header already does that,
      // so the footer is one big Place Order (or Close on the error step).
      footer={
        step === STEP_ERROR ? (
          <Button
            type="primary"
            size="large"
            block
            className="reorderModal-cont__btn"
            onClick={close}>
            Close
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            block
            className="reorderModal-cont__btn"
            loading={placing}
            disabled={!canPlace}
            onClick={placeOrder}>
            Place Order
          </Button>
        )
      }>
      {step === STEP_ERROR ? (
        renderError()
      ) : (
        <Spin spinning={Boolean(loading)} size="large">
          {preview ? (
            renderPreview()
          ) : (
            // Keeps the dialog from collapsing to zero height while the
            // preview loads.
            <div className="reorderModal-cont__placeholder" />
          )}
        </Spin>
      )}
    </Modal>
  );
}
